import React, { useState, useMemo } from "react";
import {
  Box,
  Typography,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Link,
  Chip,
  Stack,
  Pagination,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Paper,
  useTheme,
  CircularProgress,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { Edit, Delete, Visibility } from "@mui/icons-material";

// Utility to extract plain text from HTML (basic)
const htmlToText = (html) => {
  if (!html) return "No content";
  const div = document.createElement("div");
  div.innerHTML = html;
  return div.textContent || div.innerText || "";
};

function ContentList({
  contents,
  allContents = [],
  onEdit,
  onDelete,
  loading = false,
  page = 0,
  pageSize = 10,
  totalItems = 0,
  onPageChange,
  onPageSizeChange,
}) {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedContent, setSelectedContent] = useState(null);

  const handleOpenDialog = (content) => {
    setSelectedContent(content);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedContent(null);
  };

  const columns = [
    {
      field: "id",
      headerName: "ID",
      width: 80,
      type: "number",
    },
    {
      field: "title",
      headerName: "Title",
      flex: 1.5,
      minWidth: 200,
      renderCell: (params) => (
        <Typography
          variant="body2"
          sx={{
            fontWeight: 500,
            color: theme.palette.text.primary,
          }}
        >
          {params.value}
        </Typography>
      ),
    },
    {
      field: "content_id",
      headerName: "Content ID",
      flex: 1,
      minWidth: 120,
      renderCell: (params) => (
        <Chip
          label={params.value}
          size="small"
          sx={{
            fontFamily: "monospace",
            backgroundColor: isDark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.05)",
          }}
        />
      ),
    },
    {
      field: "subject",
      headerName: "Subject",
      flex: 1,
      minWidth: 120,
      renderCell: (params) => (
        <Chip
          label={params.value}
          size="small"
          color="primary"
          variant="outlined"
        />
      ),
    },
    {
      field: "preview",
      headerName: "Content Preview",
      flex: 2,
      minWidth: 250,
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        const previewText = htmlToText(params.row.content_text || "").slice(0, 80) + "...";
        return (
          <Link
            component="button"
            variant="body2"
            onClick={() => handleOpenDialog(params.row)}
            sx={{
              cursor: "pointer",
              textAlign: "left",
              color: theme.palette.primary.main,
              "&:hover": {
                textDecoration: "underline",
              },
            }}
            title="Click to view full content"
          >
            {previewText || "No content available"}
          </Link>
        );
      },
    },
    {
      field: "video_link",
      headerName: "Video Link",
      flex: 1.2,
      minWidth: 150,
      renderCell: (params) =>
        params.value ? (
          <Link
            href={params.value}
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              color: theme.palette.primary.main,
              textDecoration: "none",
              "&:hover": {
                textDecoration: "underline",
              },
            }}
          >
            View Video
          </Link>
        ) : (
          <Typography variant="body2" color="text.secondary">
            -
          </Typography>
        ),
    },
    {
      field: "actions",
      headerName: "Actions",
      sortable: false,
      filterable: false,
      align: "center",
      headerAlign: "center",
      width: 150,
      renderCell: (params) => (
        <Box display="flex" gap={0.5}>
          <Tooltip title="View Full Content">
            <IconButton
              color="info"
              onClick={() => handleOpenDialog(params.row)}
              size="small"
            >
              <Visibility fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Edit">
            <IconButton
              color="primary"
              onClick={() => onEdit(params.row)}
              size="small"
            >
              <Edit fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton
              color="error"
              onClick={() => onDelete(params.id)}
              size="small"
            >
              <Delete fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  const rows = contents.map((c) => ({
    id: c.id,
    title: c.title,
    content_id: c.content_id,
    subject: c.subject,
    content_text: c.content_text,
    video_link: c.video_link,
  }));

  const totalPages = Math.ceil(totalItems / pageSize);

  return (
    <Box sx={{ mt: 4 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="h6" fontWeight={700}>
          Existing Content
        </Typography>
        {loading && <CircularProgress size={24} />}
      </Stack>

      {contents.length === 0 && !loading ? (
        <Paper
          sx={{
            p: 4,
            textAlign: "center",
            background: isDark ? "rgba(255,255,255,0.02)" : "rgba(0,0,0,0.02)",
            borderRadius: "12px",
          }}
        >
          <Typography variant="body1" color="text.secondary">
            No content found. {totalItems > 0 ? "Try changing the filter." : "Start by adding new content above."}
          </Typography>
        </Paper>
      ) : (
        <>
          <Paper
            elevation={0}
            sx={{
              height: 600,
              width: "100%",
              borderRadius: "12px",
              overflow: "hidden",
              border: `1px solid ${isDark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.1)"}`,
            }}
          >
            <DataGrid
              rows={rows}
              columns={columns}
              pageSize={pageSize}
              rowsPerPageOptions={[]}
              pagination={false}
              disableSelectionOnClick
              disableColumnMenu
              loading={loading}
              sx={{
                border: "none",
                "& .MuiDataGrid-columnHeaders": {
                  backgroundColor: isDark ? "rgba(255,255,255,0.05)" : "#f5f5f5",
                  fontWeight: "bold",
                  borderBottom: `1px solid ${isDark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.1)"}`,
                },
                "& .MuiDataGrid-row": {
                  "&:hover": {
                    backgroundColor: isDark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.02)",
                  },
                },
                "& .MuiDataGrid-cell": {
                  borderBottom: `1px solid ${isDark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.05)"}`,
                },
              }}
            />
          </Paper>

          {/* Custom Pagination */}
          {totalPages > 1 && (
            <Stack
              direction="row"
              spacing={2}
              alignItems="center"
              justifyContent="space-between"
              sx={{ mt: 2, flexWrap: "wrap", gap: 2 }}
            >
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel>Rows per page</InputLabel>
                <Select
                  value={pageSize}
                  label="Rows per page"
                  onChange={(e) => {
                    onPageSizeChange(e.target.value);
                    onPageChange(0); // Reset to first page
                  }}
                >
                  <MenuItem value={5}>5</MenuItem>
                  <MenuItem value={10}>10</MenuItem>
                  <MenuItem value={20}>20</MenuItem>
                  <MenuItem value={50}>50</MenuItem>
                </Select>
              </FormControl>

              <Typography variant="body2" color="text.secondary">
                Showing {page * pageSize + 1} to {Math.min((page + 1) * pageSize, totalItems)} of {totalItems} items
              </Typography>

              <Pagination
                count={totalPages}
                page={page + 1}
                onChange={(event, value) => onPageChange(value - 1)}
                color="primary"
                showFirstButton
                showLastButton
              />
            </Stack>
          )}
        </>
      )}

      {/* Popup Dialog for Full Content Preview */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="lg"
        fullWidth
        scroll="paper"
        PaperProps={{
          sx: {
            borderRadius: "16px",
            background: isDark ? "rgba(26, 26, 26, 0.95)" : "#ffffff",
          },
        }}
      >
        <DialogTitle
          sx={{
            borderBottom: `1px solid ${isDark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.1)"}`,
            fontWeight: 700,
          }}
        >
          {selectedContent?.title || "Content Detail"}
        </DialogTitle>
        <DialogContent dividers sx={{ p: 3 }}>
          {selectedContent ? (
            <Box>
              <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                <Chip label={`Subject: ${selectedContent.subject}`} size="small" color="primary" />
                <Chip label={`ID: ${selectedContent.content_id}`} size="small" variant="outlined" />
              </Stack>
              <Box
                sx={{
                  whiteSpace: "normal",
                  maxHeight: "70vh",
                  overflowY: "auto",
                  "& img": { maxWidth: "100%" },
                }}
                dangerouslySetInnerHTML={{ __html: selectedContent.content_text || "No content available" }}
              />
            </Box>
          ) : (
            <Typography>No content to display.</Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCloseDialog} variant="contained">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default ContentList;
